<template>
  <v-stepper v-model="step">
    <v-stepper-items>
      <v-stepper-content :step="0" class="pa-0">
        <!-- course preview items -->
        <v-list v-if="bookingType === TYPE_COURSES" elevation="2" three-line class="pa-1" style="border-radius: 0px">
          <v-subheader> {{ $t('overview.bookable_courses') }} </v-subheader>
          <!-- love to these data iterators: https://v2.vuetifyjs.com/en/components/data-iterators/ -->
          <v-data-iterator
            disable-pagination
            hide-default-footer
            :item-key="'CourseId'"
            :items="itemsToDisplay ? itemsToDisplay : getFilteredCourseItems('')"
            :custom-filter="customCourseFilter"
            :search="searchFor"
            :no-results-text="$t('common.label_noresults')"
            :no-data-text="$t('common.label_nodata')"
            :sort-by="sortBy ? sortBy.key : undefined"
            :sort-desc="sortBy ? sortBy.sortDesc : undefined"
            class="nomargin-firstchild nomargin-lastchild">
            <template v-slot:default="props">
              <v-row no-gutters :class="currentBreakpoint == 1 ? 'nomargin-firstchild nomargin-lastchild' : ''" style="max-width: calc(100vw - 32px)">
                <CoursePreviewItem
                  v-for="(course, index) in props.items"
                  @onSelect="handleCourseSelection(course, false)"
                  :selected="hasSelection && currentSelection.CourseId === course.CourseId"
                  :loading="loadingElem === course.CourseId"
                  :disabled="loadingElem !== ''"
                  :key="'courseListItem' + index"
                  :courseId="course.CourseId" />
              </v-row>
            </template>
          </v-data-iterator>
        </v-list>

        <!-- hall preview items -->
        <v-list v-if="bookingType === TYPE_HALLS" elevation="2" three-line class="pa-1" style="border-radius: 0px">
          <v-subheader inset> {{ $t('overview.bookable_halls') }} </v-subheader>
          <v-data-iterator
            disable-pagination
            hide-default-footer
            :item-key="'Id'"
            :items="itemsToDisplay ? itemsToDisplay : getFilteredHallItems('')"
            :custom-filter="customHallFilter"
            :custom-sort="customHallSort"
            :search="searchFor"
            :no-results-text="$t('common.label_noresults')"
            :no-data-text="$t('common.label_nodata')"
            :sort-by="sortBy ? sortBy.key : undefined"
            :sort-desc="sortBy ? sortBy.sortDesc : undefined">
            <template v-slot:default="props">
              <v-row no-gutters :class="currentBreakpoint == 1 ? 'nomargin-firstchild nomargin-lastchild' : ''" style="max-width: calc(100vw - 32px)">
                <HallPreviewItem
                  v-for="(hall, index) in props.items"
                  @onSelect="handleHallSelection(hall)"
                  :selected="hasSelection && currentSelection.Id === hall.Id"
                  :loading="loadingElem === hall.Id"
                  :disabled="loadingElem !== ''"
                  :key="'hallListItem' + index"
                  :hallId="hall.Id" />
              </v-row>
            </template>
          </v-data-iterator>
        </v-list>

        <!-- preview items for my bookings -->
        <v-list v-if="bookingType === TYPE_BOOKINGS" three-line class="nomargin-firstchild nomargin-lastchild pa-1" style="border-radius: 0px">
          <!-- booked courses -->
          <v-subheader inset v-if="bookedCourses && bookedCourses.length > 0"> {{ $t('overview.mybookings_bookedCourses') }} </v-subheader>
          <v-row no-gutters :class="currentBreakpoint == 1 ? 'nomargin-firstchild nomargin-lastchild' : ''" style="max-width: calc(100vw - 32px)">
            <CoursePreviewItem
              v-for="(course, index) in bookedCourses"
              @onSelect="handleCourseSelection(course)"
              :selected="hasSelection && currentSelection.CourseId === course.CourseId"
              :loading="loadingElem === course.CourseId"
              :key="'personalBookedCourseListItem' + index"
              :courseId="course.CourseId"
              :fromMyBookings="true">
              <template slot="avatar">
                <!-- teal accent-4 is the same color as in the VereinsZeit app (#00bfa5) -->
                <v-card color="teal accent-4" width="64px" height="128px" class="mr-4 white--text">
                  <v-row no-gutters justify="center" align="center" class="text-center" style="height: 100%">
                    <v-col>
                      <div v-html="getNextMeetingCourse(course)" />
                    </v-col>
                  </v-row>
                </v-card>
              </template>
            </CoursePreviewItem>
          </v-row>
          <!-- reserved courses -->
          <v-subheader inset v-if="reservedCourses && reservedCourses.length > 0"> {{ $t('overview.mybookings_reservedCourses') }} </v-subheader>
          <v-row no-gutters :class="currentBreakpoint == 1 ? 'nomargin-firstchild nomargin-lastchild' : ''" style="max-width: calc(100vw - 32px)">
            <CoursePreviewItem
              v-for="(course, index) in reservedCourses"
              @onSelect="handleCourseSelection(course)"
              :selected="hasSelection && currentSelection.CourseId === course.CourseId"
              :loading="loadingElem === course.CourseId"
              :disabled="loadingElem !== ''"
              :key="'personalReservedCourseListItem' + index"
              :courseId="course.CourseId"
              :fromMyBookings="true">
              <template slot="avatar">
                <v-card color="teal accent-4" width="64px" height="128px" class="mr-4 white--text">
                  <v-row no-gutters justify="center" align="center" class="text-center" style="height: 100%">
                    <v-col> <div v-html="getNextMeetingCourse(course)" /> </v-col>
                  </v-row>
                </v-card>
              </template>
            </CoursePreviewItem>
          </v-row>
          <!-- booked halls -->
          <v-subheader inset v-if="bookedHallMeetings && bookedHallMeetings.length > 0"> {{ $t('overview.mybookings_bookedHalls') }} </v-subheader>
          <v-row no-gutters :class="currentBreakpoint == 1 ? 'nomargin-firstchild nomargin-lastchild' : ''" style="max-width: calc(100vw - 32px)">
            <HallPreviewItem
              v-for="(hall, index) in bookedHallMeetings"
              @onSelect="handleHallSelection(hall)"
              :selected="hasSelection && currentSelection.Id === hall.Id"
              :loading="loadingElem === hall.Id"
              :disabled="loadingElem !== ''"
              :key="'personalBookedhallListItem' + index"
              :hallId="hall.Id"
              :fromMyBookings="true">
              <template slot="avatar">
                <v-card color="teal accent-4" width="64px" height="128px" class="mr-4 white--text">
                  <v-row no-gutters justify="center" align="center" class="text-center" style="height: 100%">
                    <v-col>
                      <div v-html="getNextMeetingHall(hall)" />
                    </v-col>
                  </v-row>
                </v-card>
              </template>
            </HallPreviewItem>
          </v-row>
          <v-subheader v-if="hasBookedOrReserved">
            {{ $t('overview.mybookings_noBookings') }}
          </v-subheader>
        </v-list>
      </v-stepper-content>

      <!-- hall/course details -->
      <v-stepper-content :step="1" class="pa-0">
        <div v-if="hasSelection && loadingElem === '' && step === 1" class="fill-height">
          <CourseDetails
            v-if="bookingType === TYPE_COURSES || (bookingType == TYPE_BOOKINGS && getType(currentSelection) === TYPE_COURSES)"
            @onBackButtonClick="goBackToOverview()"
            @onBookingButtonClick="handleCourseBookingClick"
            @onReservationButtonClick="handleCourseReservationClick"
            :fromMyBookings="bookingType === TYPE_BOOKINGS"
            :isBooking="isBooking"
            :isReservating="isReservating"
            :courseId="currentSelection.CourseId" />

          <HallDetails
            v-if="bookingType === TYPE_HALLS || (bookingType == TYPE_BOOKINGS && getType(currentSelection) === TYPE_HALLS)"
            @onButtonBackClick="goBackToOverview()"
            @onBookingClick="handleHallBookingClick"
            :showBookedMeetings="bookingType == TYPE_BOOKINGS && getType(currentSelection) === TYPE_HALLS"
            :fromMyBookings="bookingType === TYPE_BOOKINGS"
            :isBooking="isBooking"
            :isReservating="isReservating"
            :hallId="currentSelection.Id" />
        </div>
      </v-stepper-content>

      <v-stepper-content :step="2" class="pa-0">
        <HallMeetings
          v-if="hasSelection && step == 2"
          :hallId="currentSelection.Id"
          @onBookingDataInput="handleHallBookingInput"
          @onMeetingClick="handleMeetingClick"
          @onBookingClick="handleBookAction"
          @onBackClick="goBackToDetails()" />
      </v-stepper-content>

      <v-stepper-content :step="3" class="pa-0">
        <PaymentDialog
          ref="paymentDialog"
          v-if="hasSelection && step === 3"
          :itemType="getType(currentSelection)"
          :itemIdToPayFor="currentSelectionId"
          :bookableItem="getBookingItem()"
          @onPaymentSuccess="handlePaymentSuccess"
          @onButtonBackClick="handlePaymentButtonBackClick" />
      </v-stepper-content>

      <v-stepper-content :step="4" class="pa-0">
        <v-row v-if="step === 4">
          <PaymentSuccess
            v-if="step === 4 && successfulPayment"
            @onButtonOverviewClick="goBackToOverviewAfterPayment()"
            @onButtonMyBookingsClick="goToMyBookings()"
            :paymentOption="successfulPayment.paymentOption"
            :price="successfulPayment.price"
            :feeAmount="successfulPayment.feeAmount"
            :fromMyBookings="bookingType === TYPE_BOOKINGS"
            :type="successfulPayment.type"
            :bookedTitle="successfulPayment.title" />
        </v-row>
      </v-stepper-content>
    </v-stepper-items>
  </v-stepper>
</template>

<script>
// vuex
import { GET_SESSIONDATA } from '@/store/storeMutator';
import { mapGetters } from 'vuex';

// mixins
import ConstantsMixin from '@/mixins/constants.js';
import HelpersMixin from '@/mixins/helpers.js';
import Sorts from '@/mixins/sorts.js';
import Responsive from '@/mixins/responsive.js';
// components
import CoursePreviewItem from '@/components/booking/CoursePreviewItem.vue';
import CourseDetails from '@/components/booking/CourseDetails.vue';
import HallPreviewItem from '@/components/booking/HallPreviewItem.vue';
import HallDetails from '@/components/booking/HallDetails.vue';
import HallMeetings from '@/components/booking/HallMeetings.vue';
import PaymentDialog, { BookableItem } from '@/components/controls/PaymentDialog.vue';
import PaymentSuccess from '@/components/controls/PaymentSuccess.vue';

export default {
  name: 'Overview',

  props: {
    bookingType: String,
    isBooking: Boolean,
    isReservating: Boolean,
    loadingElem: String,
    bookingHall: String,
    searchFor: String,
    filterFor: String,
    sortBy: Object,
  },

  components: {
    CoursePreviewItem,
    CourseDetails,
    HallPreviewItem,
    HallDetails,
    HallMeetings,
    PaymentDialog,
    PaymentSuccess,
  },

  mixins: [ConstantsMixin, HelpersMixin, Sorts, Responsive],

  emits: [
    'onPreviewItemSelect',
    'onRemoveBookingButtonClick',
    'onRemoveReservationButtonClick',
    'onAddReservationButtonClick',
    'onBookingInput',
    'onMeetingClick',
    'onFinishedPayment',
    'stepChanged',
  ],

  data: () => {
    return {
      step: 0,
      currentSelectionId: null,
      itemsToDisplay: null,
      selectedHallMeeting: null,
      successfulPayment: null,
      /** contains the bookingType (course, hall) when the user has clicked on a booked or reserved item in the "Meine Buchungen" area */
      myBookingsType: null,
    };
  },

  created() {
    if (this.bookingType === this.TYPE_COURSES) {
      this.itemsToDisplay = this.SessionData.courses;
    } else if (this.bookingType === this.TYPE_HALLS) {
      this.itemsToDisplay = this.SessionData.halls;
    }
  },

  methods: {
    // #region eventhandlers
    handlePaymentSuccess(paymentOption, price, type, title, feeAmount) {
      this.successfulPayment = {
        paymentOption: paymentOption,
        price: price,
        type: type,
        title: title,
        feeAmount: feeAmount,
      };
      this.step = 4;
    },

    // #region courses
    /** handles the selection of a CoursePreviewItem */
    handleCourseSelection(selection) {
      if (this.bookingType === this.TYPE_BOOKINGS) this.myBookingsType = this.TYPE_COURSES;

      this.$emit('onPreviewItemSelect', selection);
      this.currentSelectionId = selection.CourseId;
    },

    /** handles the click on the "Kurs Buchen" button */
    handleCourseBookingClick(courseToBeBooked) {
      if (courseToBeBooked.booked) {
        this.$emit('onRemoveBookingButtonClick', courseToBeBooked);
        return;
      }

      this.step = 3;
    },

    /** handles the click on the "Kurs Reservieren" button */
    handleCourseReservationClick(courseToBeReservated) {
      if (courseToBeReservated.reserved) {
        this.$emit('onRemoveReservationButtonClick', courseToBeReservated);
        return;
      }

      this.$emit('onAddReservationButtonClick', courseToBeReservated);
    },
    // #endregion

    // #region halls
    /** handles the selection of a HallPreviewItem */
    handleHallSelection(selection) {
      if (this.bookingType === this.TYPE_BOOKINGS) this.myBookingsType = this.TYPE_HALLS;

      this.$emit('onPreviewItemSelect', selection);
      this.currentSelectionId = selection.Id;
    },

    /** handles the click on the "Halle Buchen" button */
    async handleHallBookingClick(hallToBeBooked, removeBooking, meetingId) {
      if (hallToBeBooked.booked && removeBooking) {
        this.$emit('onRemoveBookingButtonClick', hallToBeBooked, meetingId);
        return;
      }
      this.step = 2;
    },

    /** handles the input for the booking arguments */
    handleHallBookingInput(hallToBeBooked, bookingData) {
      this.$emit('onHallBookingInput', hallToBeBooked, bookingData);
    },

    /** handles the click on a meeting while booking */
    handleMeetingClick(hall, bookingData) {
      this.$emit('onMeetingClick', hall, bookingData);
    },

    /** handles the click on the "Termin Buchen" button */
    handleBookAction(hallToBeBooked, meetingToBeBooked) {
      this.selectedHallMeeting = meetingToBeBooked;
      this.step = 3;
    },
    // #endregion

    /** handles the click on the back button from the PaymentDialog */
    handlePaymentButtonBackClick() {
      if (
        this.bookingType === this.TYPE_COURSES ||
        (this.bookingType == this.TYPE_BOOKINGS && this.getType(this.currentSelection) === this.TYPE_COURSES)
      ) {
        this.goBackToDetails();
      }

      if (this.bookingType === this.TYPE_HALLS || (this.bookingType == this.TYPE_HALLS && this.getType(this.currentSelection) === this.TYPE_HALLS)) {
        this.goBackToMeetingSelection();
      }
    },

    goToMyBookings() {
      if (this.bookingType === this.TYPE_BOOKINGS) {
        this.goBackToOverview();
        this.$emit('onFinishedPayment', false);
      }
      this.$emit('onFinishedPayment', true);
    },

    goBackToOverviewAfterPayment() {
      this.$emit('onFinishedPayment', false);
      this.goBackToOverview();
    },

    goBackToOverview() {
      this.step = 0;
      this.currentSelectionId = null;
      this.selectedHallMeeting = null;
    },

    goBackToDetails() {
      this.step = 1;
      this.selectedHallMeeting = null;
    },

    goBackToMeetingSelection() {
      this.step = 2;
    },
    // #endregion

    /** custom filter when searching in the v-data-iterator for a course (we dont need all values to search for) */
    customCourseFilter(courses, search) {
      if (!search) return courses;

      search = search.toString().toLowerCase();

      if (search.trim() === '') return courses;

      return courses.filter((course) => {
        if (
          String(course.CourseTitle.toLowerCase().trim()).includes(search) ||
          (course.CourseDesignation && String(course.CourseDesignation.toLowerCase().trim()).includes(search))
        )
          return course;
      });
    },

    /** returns filtered course items */
    getFilteredCourseItems(filter) {
      if (this.isNoWs(filter)) {
        return this.SessionData.club.courses;
      }
      var filteredItems = this.SessionData.club.courses.filter((course) => {
        if (course[filter]) return course;
      });

      return filteredItems;
    },

    /**
     * contains the sorting sequences for hall sorting
     *
     * @param {Array} halls halls that should be sorted
     * @param {Array} sortBy by which field of the hall object should be filtered
     * @param {Boolean} sortDescending whether they should be sorted in ascending (false) or descending (true) order
     */
    customHallSort(halls, sortBy, sortDescending) {
      if (sortBy.length === 0) return halls;

      // these will be passed as an array, so we take the first item of it
      var sortKey = sortBy[0];
      var sortDesc = sortDescending[0];

      var sortedHalls = [];

      switch (sortKey) {
        // sorting by hall.Title
        case this.SORT_HALL_TITLE:
          sortedHalls = this.hallTitleSort(halls, sortDesc);
          break;
        // sorting by hall.Area
        case this.SORT_HALL_AREA:
          sortedHalls = this.hallAreaSort(halls, sortDesc);
          break;
        // sorting by hall.TypesOfUse
        case this.SORT_HALL_TOU:
          sortedHalls = this.hallTOUSort(halls, sortDesc);
          break;
      }

      return sortedHalls;
    },

    /** custom filter when searching in the v-data-iterator for a hall (we dont need all values to search for) */
    customHallFilter(halls, search) {
      if (!search) return halls;

      search = search.toString().toLowerCase();

      if (search.trim() === '') return halls;

      return halls.filter((hall) => {
        let typesOfUseTitles = hall.tags.map((tag) => tag.title.trim().toLowerCase());

        if (hall.booked) {
          // remove 'gebucht' and ' | '
          typesOfUseTitles.splice(0, 2);
        }

        let joinedTypesOfUseTitles = typesOfUseTitles.join(' ');

        if (String(hall.Title.toLowerCase().trim()).includes(search) || joinedTypesOfUseTitles.includes(search)) return hall;
      });
    },

    /** returns filtered hall items */
    getFilteredHallItems(filter) {
      if (this.isNoWs(filter)) {
        return this.SessionData.club.halls;
      }
      var filteredItems = this.SessionData.club.halls.filter((hall) => {
        if (hall[filter]) return hall;
      });

      return filteredItems;
    },

    /** returns an normalized booking object the PaymentDialog can work with */
    getBookingItem() {
      var type = this.getType(this.currentSelection);

      if (type === this.TYPE_COURSES) {
        let itemToBook = new BookableItem(
          this.currentSelection.CourseImage,
          this.currentSelection.CourseTitle,
          this.currentSelection.CourseDesignation,
          this.currentSelection.RegularPrice,
          this.currentSelection.CourseId,
          this.currentSelection,
        );

        return itemToBook;
      } else if (type === this.TYPE_HALLS) {
        if (this.selectedHallMeeting === null) return null;

        let itemToBook = new BookableItem(
          this.currentSelection.Image,
          this.currentSelection.Title,
          this.currentSelection.details.Description,
          this.selectedHallMeeting.selectedMeeting.rent,
          this.currentSelection.Id,
          this.selectedHallMeeting,
        );

        return itemToBook;
      }
    },
  },

  computed: {
    ...mapGetters({ SessionData: GET_SESSIONDATA }),

    currentSelection() {
      if (this.currentSelectionId === null) return null;

      if (this.bookingType === this.TYPE_COURSES || this.myBookingsType === this.TYPE_COURSES) {
        var course = this.SessionData.club.courses.filter((course) => course.CourseId == this.currentSelectionId)[0];
        if (course === undefined) return null;

        return course;
      } else if (this.bookingType === this.TYPE_HALLS || this.myBookingsType === this.TYPE_HALLS) {
        var hall = this.SessionData.club.halls.filter((hall) => hall.Id == this.currentSelectionId)[0];
        if (hall === undefined) return null;

        return hall;
      }
      return null;
    },

    hasSelection() {
      return this.currentSelection !== null;
    },

    hasBookedOrReserved() {
      return (
        this.bookedCourses &&
        !this.bookedCourses.length > 0 &&
        this.reservedCourses &&
        !this.reservedCourses.length > 0 &&
        this.bookedHallMeetings &&
        !this.bookedHallMeetings.length > 0
      );
    },

    //#region my bookings

    /** returns all booked courses */
    bookedCourses() {
      return this.SessionData.club.courses ? this.SessionData.club.courses.filter((course) => course.booked) : undefined;
    },

    /** returns all reserved courses */
    reservedCourses() {
      return this.SessionData.club.courses ? this.SessionData.club.courses.filter((course) => course.reserved) : undefined;
    },

    /** returns all booked hall meetings */
    bookedHallMeetings() {
      return this.SessionData.club.halls ? this.SessionData.club.halls.filter((hall) => hall.booked) : undefined;
    },

    // #endregion
  },

  watch: {
    /**
     * this will take care of the loading-state
     *
     * the problem:
     * 1. user clicks on the preview card
     * 2. 'onPreviewItemSelect' is triggered
     * 3. Index.vue loads details and updates the vuex store
     * --> how long does this take, when can I show the details?
     *
     * the solution:
     *
     * 'loadingElem' is set to "" after finishing loading the data
     *
     * --> as soon as this is the case the details will be shown
     */
    async loadingElem(newVal, oldVal) {
      if (oldVal !== '' && newVal === '') {
        await this.$nextTick().then(() => {
          this.step++;
        });
      }
    },

    async isReservating(newVal, oldVal) {
      if (oldVal && !newVal) {
        if (this.bookingType === this.TYPE_BOOKINGS) {
          this.goBackToOverview();
        }
      }
    },

    async isBooking(newVal, oldVal) {
      if (oldVal && !newVal) {
        if (this.bookingType === this.TYPE_BOOKINGS) {
          // removing an booking from a course --> back to overview
          if (this.myBookingsType === this.TYPE_COURSES) {
            this.goBackToOverview();
          }

          // removing an booked meeting a hall --> check if a booked meeting exist
          if (this.myBookingsType === this.TYPE_HALLS && !this.currentSelection.booked) {
            this.goBackToOverview();
          }
        }
      }
    },

    filterFor(newFilter) {
      var filteredItems = [];

      // course filter
      if (this.bookingType === this.TYPE_COURSES) {
        filteredItems = this.getFilteredCourseItems(newFilter);
        // hall filter
      } else if (this.bookingType === this.TYPE_HALLS) {
        filteredItems = this.getFilteredHallItems(newFilter);
        // my bookings filter
      } else if (this.bookingType === this.TYPE_BOOKINGS) {
        filteredItems = null;
      }

      this.itemsToDisplay = filteredItems;
    },

    /** emits the "stepChanged" event */
    step(newStep) {
      this.$emit('stepChanged', newStep);
    },
  },
};
</script>
